import { useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/Button";
import CustomTabs from "../../components/CustomTabs";
import { Flex } from "../../components/Flex";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { useRetrieveProfile } from "../../data/useRetrieveProfile";
import theme from "../../theme";
import { H3 } from "../../components/Heading";
import { Box } from "../../components/Box";
import { ROUTE } from "../../routes";
import {
  ProfileStatusColours,
  ProfileStatusBgColours,
} from "../../components/layoutConfig";
import { ReviewStatusPill } from "../../components/ReviewStatusPill";
import { HiOutlineIdentification, HiOutlineShare } from "react-icons/hi2";
import { ProfileStatusSlug } from "../../context/ProfileStatusContext";
import RelationshipSlider from "./ProfileOrgTab/tabs/RelationshipSlider";
import { useOnClickOutside } from "../../utils/useOnClickOutside";
import { useAuth } from "../../context/AuthContext";
import { RecordSharing } from "../../components/RecordAccess";

export const ProfileDetails = () => {
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [addRelationshipBoxOpen, setAddRelationshipBoxOpen] = useState(false);
  const [firstProfile, setFirstProfile] = useState<any>(null);
  const createMenuRef = useRef<HTMLDivElement | null>(null);

  useOnClickOutside(createMenuRef, () => setIsCreateOpen(false));

  const profileQuery = useRetrieveProfile(profileId);
  const profile = profileQuery?.data;

  const activeTopTabIndex = useMemo(() => {
    return location.pathname.endsWith("/org") ? 1 : 0;
  }, [location.pathname]);

  const handleTopTabChange = (index: number) => {
    if (index === 0) navigate(`/profile/${profileId}`);
    if (index === 1) navigate(`/profile/${profileId}/org`);
  };

  // Initial load only — a background refetch (e.g. after a sharing change)
  // must not unmount the page and the open Access panel.
  if (profileQuery.isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" minHeight="25vh">
        <Loader />
      </Flex>
    );
  }

  if (profileQuery.isError || !profile) {
    return (
      <Paragraph m={3} color="gray.40" textAlign="center">
        Failed to load profile
      </Paragraph>
    );
  }

  const isEntity = profile?._type === "entity";

  const displayName = isEntity
    ? profile?.name ?? "-"
    : [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
      "-";

  const handleCreateSelection = (
    type: "application" | "screening" | "monitoring" | "relationship",
  ) => {
    setIsCreateOpen(false);

    if (type === "application") {
      navigate(
        ROUTE.PROFILE_APPLICATION_ENTITY_DETAILS.replace(
          ":id",
          profileId.toString(),
        ),
      );
      return;
    }

    if (type === "screening") {
      navigate(
        ROUTE.PROFILE_SCREENING_SELECT_CHECKS.replace(
          ":id",
          profileId.toString(),
        ),
      );
      return;
    }

    if (type === "monitoring") {
      navigate(
        ROUTE.PROFILE_MONITORING_DETAILS.replace(":id", profileId.toString()),
      );
      return;
    }

    if (type === "relationship") {
      setFirstProfile(profile);
      setAddRelationshipBoxOpen(true);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Flex alignItems="center" gap="6" fontSize={16}>
          <H3
            onClick={() => navigate(ROUTE.PROFILES)}
            style={{
              fontWeight: 700,
              color: theme.colors.gray[80],
              cursor: "pointer",
            }}
          >
            Profiles
          </H3>
          <span style={{ color: theme.colors.gray[40] }}>/</span>
          <H3 fontWeight={700} color={theme.colors.blue.primary}>
            {displayName}
          </H3>

          {profile?.status?.label && (
            <Box>
              {(() => {
                const slug = (profile.status.status ?? "") as ProfileStatusSlug;

                const color = ProfileStatusColours[slug] ?? "gray.60";
                const bgColor = ProfileStatusBgColours[slug] ?? "gray.10";

                return (
                  <ReviewStatusPill
                    label={profile.status.label}
                    color={color}
                    bgColor={bgColor}
                  />
                );
              })()}
            </Box>
          )}
        </Flex>

        <Flex alignItems="center" gap="16">
          <RecordSharing
            module="profile"
            recordId={profileId}
            recordName={displayName}
            recordReference={profile?.reference}
            noun="profile"
            primaryDivision={profile?.primary_division}
            primaryTeam={profile?.primary_team}
            additionalDivisions={profile?.additional_divisions}
            additionalTeams={profile?.additional_teams}
            invalidateKeys={[["profile", profileId]]}
          />

          <div style={{ minWidth: 220 }}>
            <CustomTabs
              variant="segmented"
              navOnly
              tabs={[
                {
                  label: (
                    <>
                      <HiOutlineIdentification size={16} />
                      Profile
                    </>
                  ),
                  content: null,
                },
                {
                  label: (
                    <>
                      <HiOutlineShare size={16} />
                      Organisation
                    </>
                  ),
                  content: null,
                },
              ]}
              activeTab={activeTopTabIndex}
              onTabChange={handleTopTabChange}
            />
          </div>

          <Box position="relative" ref={createMenuRef}>
            <Button onClick={() => setIsCreateOpen((s) => !s)}>Create</Button>

            {isCreateOpen && (
              <Box
                style={{
                  position: "absolute",
                  top: "110%",
                  right: 0,
                  background: "white",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.12)",
                  borderRadius: "12px",
                  zIndex: 30,
                  width: "220px",
                  overflow: "hidden",
                }}
              >
                <Paragraph
                  my={0}
                  style={{
                    padding: "10px 12px",
                    cursor: hasPermission("mandate.create")
                      ? "pointer"
                      : "not-allowed",
                    opacity: hasPermission("mandate.create") ? 1 : 0.4,
                  }}
                  onClick={
                    hasPermission("mandate.create")
                      ? () => handleCreateSelection("application")
                      : undefined
                  }
                  title={
                    hasPermission("mandate.create")
                      ? undefined
                      : "You don't have permission to create applications"
                  }
                >
                  Application
                </Paragraph>

                <Paragraph
                  my={0}
                  style={{
                    padding: "10px 12px",
                    cursor: hasPermission("screening.run")
                      ? "pointer"
                      : "not-allowed",
                    opacity: hasPermission("screening.run") ? 1 : 0.4,
                  }}
                  onClick={
                    hasPermission("screening.run")
                      ? () => handleCreateSelection("screening")
                      : undefined
                  }
                  title={
                    hasPermission("screening.run")
                      ? undefined
                      : "You don't have permission to run screenings"
                  }
                >
                  Screening
                </Paragraph>

                <Paragraph
                  my={0}
                  style={{
                    padding: "10px 12px",
                    cursor: hasPermission("monitoring.create")
                      ? "pointer"
                      : "not-allowed",
                    opacity: hasPermission("monitoring.create") ? 1 : 0.4,
                  }}
                  onClick={
                    hasPermission("monitoring.create")
                      ? () => handleCreateSelection("monitoring")
                      : undefined
                  }
                  title={
                    hasPermission("monitoring.create")
                      ? undefined
                      : "You don't have permission to create monitoring records"
                  }
                >
                  Monitoring
                </Paragraph>

                <Paragraph
                  my={0}
                  style={{ padding: "10px 12px", cursor: "pointer" }}
                  onClick={() => handleCreateSelection("relationship")}
                >
                  Add Relationship
                </Paragraph>
              </Box>
            )}
          </Box>
        </Flex>
      </Flex>

      <Outlet context={{ profile }} />

      <RelationshipSlider
        addRelationshipBoxOpen={addRelationshipBoxOpen}
        setAddRelationshipBoxOpen={setAddRelationshipBoxOpen}
        firstProfile={firstProfile}
      />
    </div>
  );
};
